"use client";

import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { GrowthChart } from "@/components/tools/biology-lab/growth-chart";
import type { CellDoublingTimeCalculatorStrings } from "@/components/tools/cell-doubling-time-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  fixed,
  type NumberFormatter,
  plain,
  scientific,
  sig,
} from "@/lib/tool-kit/biology-lab/format";
import {
  countAfter,
  type DoublingResult,
  doublingFromCounts,
  type GrowthError,
  hoursToUnits,
  type TimeUnit,
  timeToReach,
  unitsToHours,
} from "@/lib/tool-kit/biology-lab/growth";

type Strings = CellDoublingTimeCalculatorStrings;

function errorText(code: GrowthError, strings: Strings): string {
  const messages: Record<GrowthError, string> = {
    initial: strings.errInitial,
    final: strings.errFinal,
    time: strings.errTime,
    doubling: strings.errDoubling,
    target: strings.errTarget,
  };
  return messages[code];
}

function shortUnit(unit: TimeUnit, strings: Strings): string {
  const labels: Record<TimeUnit, string> = {
    min: strings.shortMin,
    h: strings.shortHour,
    d: strings.shortDay,
  };
  return labels[unit];
}

function longUnit(unit: TimeUnit, strings: Strings): string {
  const labels: Record<TimeUnit, string> = {
    min: strings.unitMin,
    h: strings.unitHour,
    d: strings.unitDay,
  };
  return labels[unit];
}

const SCIENTIFIC_FROM = 1e4;

/** A cell number: plain below 10,000, scientific notation above. */
function count(fmt: NumberFormatter, value: number): string {
  return value >= SCIENTIFIC_FROM
    ? scientific(fmt, value, 3)
    : sig(fmt, value, 4);
}

/** The same duration written in every unit, for a doubling time. */
function equivalents(
  value: number,
  unit: TimeUnit,
  strings: Strings,
  fmt: NumberFormatter
): string {
  const hours = unitsToHours(value, unit);
  const units: TimeUnit[] = ["min", "h", "d"];
  return units
    .map(
      (u) => `${sig(fmt, hoursToUnits(hours, u), 4)} ${shortUnit(u, strings)}`
    )
    .join(" = ");
}

function Chart({
  initial,
  final,
  elapsed,
  doublingTime,
  unit,
  fmt,
  strings,
}: {
  initial: number;
  final: number;
  elapsed: number;
  doublingTime: number;
  unit: TimeUnit;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const u = longUnit(unit, strings);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.chartHeading}</h3>
      <GrowthChart
        doublingTime={doublingTime}
        elapsed={elapsed}
        final={final}
        fmt={fmt}
        initial={initial}
        label={t(strings.chartLabel, {
          initial: count(fmt, initial),
          final: count(fmt, final),
          time: sig(fmt, elapsed, 3),
          unit: u,
        })}
        xLabel={t(strings.chartX, { unit: u })}
        yLabel={strings.chartY}
      />
    </div>
  );
}

function DoublingView({
  result,
  initial,
  final,
  elapsed,
  unit,
  fmt,
  strings,
}: {
  result: DoublingResult;
  initial: number;
  final: number;
  elapsed: number;
  unit: TimeUnit;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const u = shortUnit(unit, strings);
  const summary = t(strings.copyDoubling, {
    td: sig(fmt, result.doublingTime, 4),
    unit: u,
    generations: sig(fmt, result.generations, 3),
    t: plain(fmt, elapsed),
  });
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={`${strings.doublingTime} (${u})`}
          value={sig(fmt, result.doublingTime, 4)}
        />
        <StatCard
          label={strings.generations}
          value={sig(fmt, result.generations, 4)}
        />
        <StatCard
          label={t(strings.rate, { unit: u })}
          value={sig(fmt, result.rate, 4)}
        />
        <StatCard label={strings.fold} value={sig(fmt, result.foldChange, 4)} />
        <StatCard
          label={`${t(strings.percent, { unit: u })} (%)`}
          value={fixed(fmt, result.percentPerUnit, 2)}
        />
      </StatGrid>
      <p className="text-muted-foreground text-sm">
        {t(strings.alsoSame, {
          parts: equivalents(result.doublingTime, unit, strings, fmt),
        })}
      </p>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        <p className="font-medium font-sans text-foreground">
          {strings.workingHeading}
        </p>
        <p>
          {t(strings.workingDoubling, {
            t: plain(fmt, elapsed),
            final: count(fmt, final),
            initial: count(fmt, initial),
            result: sig(fmt, result.doublingTime, 4),
            unit: u,
          })}
        </p>
        <p>
          {t(strings.workingRate, {
            logRatio: sig(fmt, Math.log(final / initial), 4),
            t: plain(fmt, elapsed),
            result: sig(fmt, result.rate, 4),
            unit: u,
          })}
        </p>
        <p>
          {t(strings.workingGenerations, {
            result: sig(fmt, result.generations, 4),
          })}
        </p>
      </div>
      <Chart
        doublingTime={result.doublingTime}
        elapsed={elapsed}
        final={final}
        fmt={fmt}
        initial={initial}
        strings={strings}
        unit={unit}
      />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summary}
      />
    </>
  );
}

export function DoublingMode({
  initial,
  final,
  time,
  unit,
  fmt,
  strings,
}: {
  initial: number;
  final: number;
  time: number;
  unit: TimeUnit;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const outcome = doublingFromCounts(initial, final, time);
  if (outcome.kind === "error") {
    return <ErrorNotice>{errorText(outcome.error, strings)}</ErrorNotice>;
  }
  if (outcome.kind === "none") {
    return <Notice>{strings.none}</Notice>;
  }
  if (outcome.kind === "decline") {
    return (
      <Notice>
        {t(strings.decline, {
          half: sig(fmt, outcome.halfLife, 4),
          unit: shortUnit(unit, strings),
          generations: sig(fmt, Math.abs(outcome.generations), 3),
        })}
      </Notice>
    );
  }
  return (
    <DoublingView
      elapsed={time}
      final={final}
      fmt={fmt}
      initial={initial}
      result={outcome.result}
      strings={strings}
      unit={unit}
    />
  );
}

export function FinalMode({
  initial,
  doubling,
  time,
  unit,
  fmt,
  strings,
}: {
  initial: number;
  doubling: number;
  time: number;
  unit: TimeUnit;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const out = countAfter(initial, doubling, time);
  if ("error" in out) {
    return <ErrorNotice>{errorText(out.error, strings)}</ErrorNotice>;
  }
  const u = shortUnit(unit, strings);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.finalCount, { time: plain(fmt, time), unit: u })}
          value={count(fmt, out.value)}
        />
        <StatCard
          label={strings.generations}
          value={sig(fmt, out.generations, 4)}
        />
      </StatGrid>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        <p className="font-medium font-sans text-foreground">
          {strings.workingHeading}
        </p>
        <p>
          {t(strings.workingFinal, {
            initial: count(fmt, initial),
            t: plain(fmt, time),
            td: plain(fmt, doubling),
            result: count(fmt, out.value),
          })}
        </p>
      </div>
      {time > 0 ? (
        <Chart
          doublingTime={doubling}
          elapsed={time}
          final={out.value}
          fmt={fmt}
          initial={initial}
          strings={strings}
          unit={unit}
        />
      ) : null}
    </>
  );
}

export function TimeMode({
  initial,
  target,
  doubling,
  unit,
  fmt,
  strings,
}: {
  initial: number;
  target: number;
  doubling: number;
  unit: TimeUnit;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const out = timeToReach(initial, target, doubling);
  if ("error" in out) {
    return <ErrorNotice>{errorText(out.error, strings)}</ErrorNotice>;
  }
  const u = shortUnit(unit, strings);
  const show = out.time > 0;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={`${strings.timeNeeded} (${u})`}
          value={sig(fmt, out.time, 4)}
        />
        <StatCard
          label={strings.generations}
          value={sig(fmt, out.generations, 4)}
        />
      </StatGrid>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        <p className="font-medium font-sans text-foreground">
          {strings.workingHeading}
        </p>
        <p>
          {t(strings.workingTime, {
            td: plain(fmt, doubling),
            target: count(fmt, target),
            initial: count(fmt, initial),
            result: sig(fmt, out.time, 4),
            unit: u,
          })}
        </p>
      </div>
      {show ? (
        <Chart
          doublingTime={doubling}
          elapsed={out.time}
          final={target}
          fmt={fmt}
          initial={initial}
          strings={strings}
          unit={unit}
        />
      ) : null}
    </>
  );
}
